<script setup lang="ts">
// Предупреждение оболочки о том, что в системе нет работающего хранилища ключей.
//
// Состояние настоящее и важное: `safeStorage` в Linux без keyring не отказывает, а предлагает
// «basic text» — открытый текст под видом шифрования. Молчание здесь означало бы, что человек считает
// переписку защищённой, а она лежит открыто.
//
// Подача, однако, была неверной, и это правка по сверке Дизайна:
//
// 1. Цвет — `warning`, а не `danger`. Красный у нас означает «не сработало»: сбой, ошибка отправки.
//    Здесь приложение исправно, небезопасна среда. Красное на старте читается как поломка, а красное,
//    которое человек не может погасить сам (нужен пакет и права администратора), через неделю
//    перестаёт читаться вовсе — вместе со всем остальным красным.
// 2. Одна строка вместо трёх. Постоянный трёхстрочный баннер съедает высоту у экрана, где от человека
//    всё равно ничего не зависит. В строке — факт и последствие, подробности по кнопке.
//
// В подробностях сказано и то, чего НЕ происходит: иначе человек сделает неверный вывод о масштабе
// беды и решит, что переписка компании тоже под угрозой.
import { computed, ref } from "vue";

import { useSession } from "../stores/session.ts";

const session = useSession();
const warning = computed(() => (session.shellInfo?.secrets.encryption === false ? session.shellInfo.secrets.warning : ""));
const dialog = ref<HTMLDialogElement | undefined>(undefined);
const copied = ref(false);
const command = "sudo apt install gnome-keyring";

async function copy(): Promise<void> {
  try {
    await navigator.clipboard.writeText(command);
    copied.value = true;
  } catch {
    // Буфер обмена может быть недоступен — команда видна на экране, её можно выделить руками.
  }
}
</script>

<template>
  <div v-if="warning" class="y-banner warning">
    <span>Нет системного хранилища ключей: вход придётся выполнять при каждом запуске.</span>
    <button class="y-btn" @click="dialog?.showModal()">Подробнее</button>
  </div>
  <!-- Разметка диалога — из эталона Дизайна (`design/components/dialog.md`): свой я собрала на месте,
       пока компонента не было, и он же стал поводом его описать. -->
  <dialog ref="dialog" class="y-dialog">
    <div class="y-dialog-head">Нет системного хранилища ключей</div>
    <div class="y-dialog-body">
      <p>{{ warning }}</p>
      <p>
        <b>Что при этом в порядке:</b> сообщения на сервере компании и у остальных участников не
        затронуты — речь только об этом устройстве и только о том, что на нём негде хранить ключ.
      </p>
      <p>Установить хранилище (GNOME; в KDE — <code>kwallet</code>):</p>
    </div>
    <!-- Команду интерфейс показывает, но не выполняет — то же правило, что в самопроверке узла. -->
    <div class="y-dialog-cmd">
      <code>{{ command }}</code>
      <button class="y-btn" @click="copy">{{ copied ? "Скопировано" : "Скопировать" }}</button>
    </div>
    <div class="y-dialog-actions">
      <button class="y-btn primary" @click="dialog?.close()">Закрыть</button>
    </div>
  </dialog>
</template>
